import { useState, useMemo, useCallback } from 'react'
import { MetricData } from '../utils/metricTableHelpers'

interface ModalState {
  isOpen: boolean
  metricKey: string | null
}

interface UseMetricModalProps {
  allMetrics: MetricData[]
}

interface UseMetricModalReturn {
  /** Whether the modal is currently open */
  isOpen: boolean
  /** The currently selected metric key */
  metricKey: string | null
  /** The selected metric data object */
  selectedMetric: MetricData | undefined
  /** Handler to open the modal for a specific metric */
  handleAuthorizedClick: (metricKey: string) => void
  /** Handler to close the modal */
  handleCloseModal: () => void
}

/**
 * Hook for managing the metric source modal state
 * Handles opening/closing the modal and tracking the selected metric
 */
export const useMetricModal = ({
  allMetrics,
}: UseMetricModalProps): UseMetricModalReturn => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    metricKey: null,
  })

  const handleAuthorizedClick = useCallback((metricKey: string) => {
    setModalState({
      isOpen: true,
      metricKey,
    })
  }, [])

  const handleCloseModal = useCallback(() => {
    setModalState({ isOpen: false, metricKey: null })
  }, [])

  const selectedMetric = useMemo(() => {
    return allMetrics.find((m) => m.key === modalState.metricKey)
  }, [allMetrics, modalState.metricKey])

  return {
    isOpen: modalState.isOpen,
    metricKey: modalState.metricKey,
    selectedMetric,
    handleAuthorizedClick,
    handleCloseModal,
  }
}
